import{
 Alert,AppBar,Avatar,Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Divider,Drawer,IconButton,List,ListItemButton,
 ListItemIcon,ListItemText,Stack,Toolbar,Tooltip,Typography,useMediaQuery
}from'@mui/material';
import{alpha,useTheme}from'@mui/material/styles';
import{
 AccountBalanceWalletOutlined,AdminPanelSettingsOutlined,ChevronLeftOutlined,
 ChevronRightOutlined,DashboardOutlined,FolderOutlined,GroupsOutlined,
 InsightsOutlined,LogoutOutlined,MenuOutlined,PeopleOutline,ReceiptLongOutlined,
 SettingsApplicationsOutlined,TimelineOutlined,AutoAwesomeOutlined,MenuBookOutlined,PaletteOutlined
}from'@mui/icons-material';
import{NavLink,Outlet,useLocation,useNavigate}from'react-router-dom';
import{
 api,can,canModule,clearPlatformReturnSession,expireSessionForRelease,getSession,post,setSession,
 takePlatformReturnSession
}from'./api';
import{GlobalActivity,RouteLoadingState}from'./components';
import RasanaLogo from'./RasanaLogo';
import{RASANA_BUILD}from'./releaseBuild';
import{rasanaTokens,loadAppearance,saveAppearance,appearancePalettes,type RasanaAppearance}from'./theme';
import{Suspense,useEffect,useState}from'react';

const expandedWidth=276,collapsedWidth=76;
const preferenceKey='rasana.sidebar.collapsed';
const RELEASE_CHECK_MS=60_000;

const loginAppearanceOptions=[
 {id:'momentum',label:'Executive Momentum',detail:'Confident navy and teal'},
 {id:'calm',label:'Calm Intelligence',detail:'Warm neutral and reassuring'},
 {id:'intelligent',label:'Intelligent Workspace',detail:'Luminous blue and teal'}
]as const;
type LoginAppearance=RasanaAppearance;

const groups=[
 {label:'Delivery',items:[
  ['Projects','/projects','project.view','projects',FolderOutlined],
  ['Value Streams','/value-streams','project.view','projects',TimelineOutlined],
  ['Resources','/resources','resource.view','resources',PeopleOutline],
  ['Department Resources','/resources/departments','resource.view','resources',PeopleOutline],
  ['Shift & Rosters','/resources/roster','resource.view','resources',PeopleOutline],
  ['Roadmap','/roadmap','project.view','projects',TimelineOutlined],
  ['GCI Library','/gci','project.view','projects',MenuBookOutlined]
 ]},
 {label:'People & HR',items:[
  ['Human Resources','/hr','resource.view','resources',PeopleOutline],
  ['Workforce Intelligence','/workforce-intelligence','hr.workforce.view','resources',InsightsOutlined]
 ]},
 {label:'Commercial',items:[
  ['Customer Relationships','/crm','crm.view','core',GroupsOutlined],
  ['Sales & Pipeline','/sales','sales.view','projects',TimelineOutlined],
  ['Finance','/finance','finance.view','finance',AccountBalanceWalletOutlined],
  ['Billing','/billing','billing.view','billing',ReceiptLongOutlined]
 ]},
 {label:'Governance',items:[
  ['Attention Centre','/attention','reporting.guidance.read','reporting',AutoAwesomeOutlined],
  ['Executive Dashboard','/executive','reporting.view','reporting',InsightsOutlined],
  ['Internal SteerCo','/steerco','reporting.view','reporting',GroupsOutlined]
 ]},
 {label:'Administration',items:[
  ['Administration','/admin','organisation.manage','core',AdminPanelSettingsOutlined],
  ['Control Centre','/platform','platform.view','core',SettingsApplicationsOutlined]
 ]}
]as const;

const platformGroups=[{
 label:'Rasana Platform',items:[
  ['Control Centre','/platform','platform.view','core',AdminPanelSettingsOutlined],
  ['Subscriptions & Trials','/platform/subscriptions','platform.view','core',AccountBalanceWalletOutlined],
  ['Templates & Defaults','/platform/templates','platform.view','core',SettingsApplicationsOutlined],
   ['Database & Fields','/platform/data-model','platform.view','core',SettingsApplicationsOutlined],
  ['Activity & Audit','/platform/activity','platform.view','core',InsightsOutlined]
 ]
}]as const;

function initials(name?:string){
 return(name??'R').split(/\s+/).filter(Boolean).slice(0,2)
  .map(x=>x[0]).join('').toUpperCase();
}

export default function AppShell(){
 const nav=useNavigate();
 const location=useLocation();
 const theme=useTheme();
 const mobile=useMediaQuery(theme.breakpoints.down('md'));
 const s=getSession();
 const platformSession=s?.kind==='platform';
 const support=s?.supportAccess;
 const onboarding=s?.onboardingAccess;

 const[collapsed,setCollapsed]=useState(()=>{
  try{return localStorage.getItem(preferenceKey)==='true';}
  catch{return false;}
 });
 const[mobileOpen,setMobileOpen]=useState(false);
 const[moduleState,setModuleState]=useState(()=>({
  modules:getSession()?.businessModules??[],
  catalogue:getSession()?.moduleCatalogue??[]
 }));
 const[modulesVerified,setModulesVerified]=useState(true);
 useEffect(()=>{
  if(!s||s.kind!=='organisation'||s.onboardingAccess||s.supportAccess)return;
  let active=true;
  const refresh=async()=>{
   try{
    const updated=await api<any>('/api/v1/auth/module-access');
    if(!active||!Array.isArray(updated.modules)||!Array.isArray(updated.catalogue))return;
    const existing=getSession();
    if(existing&&existing.token===s.token)
     setSession({...existing,businessModules:updated.modules,
      moduleCatalogue:updated.catalogue,moduleRestricted:updated.restricted===true});
    setModuleState({modules:updated.modules,catalogue:updated.catalogue});
    setModulesVerified(true);
   }catch{
    if(active)setModulesVerified(false);
   }
  };
  void refresh();
  const timer=window.setInterval(()=>void refresh(),30_000);
  window.addEventListener('focus',refresh);
  return()=>{active=false;window.clearInterval(timer);window.removeEventListener('focus',refresh);};
 },[s?.token]);
 const allowedBusiness=(path:string)=>{
  if(platformSession||onboarding||support)return true;
  const matches=moduleState.catalogue.flatMap(m=>m.paths.map(prefix=>({id:m.id,prefix})))
   .filter(m=>path===m.prefix||path.startsWith(m.prefix+'/'))
   .sort((a,b)=>b.prefix.length-a.prefix.length);
  return matches.length===0||moduleState.modules.includes(matches[0].id);
 };

 const[returning,setReturning]=useState(false);
 const[appearanceOpen,setAppearanceOpen]=useState(false);
 const[loginAppearance,setLoginAppearance]=useState<LoginAppearance>(loadAppearance);
 const currentPalette=appearancePalettes[loginAppearance];
 const updateLoginAppearance=(choice:LoginAppearance)=>{
  setLoginAppearance(choice);
  saveAppearance(choice);
 };

 const compact=!mobile&&collapsed;
 const drawerWidth=compact?collapsedWidth:expandedWidth;

 const changeCollapsed=(value:boolean)=>{
  setCollapsed(value);
  try{localStorage.setItem(preferenceKey,String(value));}catch{}
 };

 useEffect(()=>{
  if(!s?.releaseId)return;

  let cancelled=false;

  const checkRelease=async()=>{
   try{
    const response=await fetch(
     `/version.json?t=${Date.now()}`,
     {cache:'no-store'}
    );

    if(!response.ok||cancelled)return;

    const version=await response.json();
    const deployedRelease=String(version?.releaseId??'').trim();

    if(
     deployedRelease&&
     deployedRelease!==s.releaseId
    ){
     expireSessionForRelease();
     window.location.replace('/login');
    }
   }catch{
    // A temporary static-file/network failure must never sign the user out.
   }
  };

  const onFocus=()=>void checkRelease();
  const onVisibility=()=>{
   if(document.visibilityState==='visible'){
    void checkRelease();
   }
  };

  const timer=window.setInterval(
   ()=>void checkRelease(),
   RELEASE_CHECK_MS
  );

  window.addEventListener('focus',onFocus);
  document.addEventListener('visibilitychange',onVisibility);
  void checkRelease();

  return()=>{
   cancelled=true;
   window.clearInterval(timer);
   window.removeEventListener('focus',onFocus);
   document.removeEventListener('visibilitychange',onVisibility);
  };
 },[s?.releaseId]);

 const logout=()=>{
  setSession(null);
  clearPlatformReturnSession();
  nav('/login');
 };

 const returnToPlatform=async()=>{
  if(!support&&!onboarding)return;
  const platform=takePlatformReturnSession();
  if(!platform){logout();return;}
  const organisationId=s?.organisation?.id;
  setReturning(true);
  setSession(platform);
  try{
   if(support){
    await post('/api/v1/platform/support-access/'+support.grantId+'/revoke',{});
   }
  }catch(error){
   console.warn('[Rasana temporary access] Cleanup could not be confirmed.',error);
  }finally{
   setReturning(false);
   nav(
    onboarding&&organisationId
     ?'/platform/organisations/'+organisationId+'/onboarding'
     :'/platform'
   );
  }
 };

 const roleLabel=platformSession
  ?'Platform Administrator'
  :s?.roles?.length?s.roles.join(' · '):'Organisation User';

 const visibleGroups=(platformSession?platformGroups:groups).map(group=>({
  ...group,
  items:group.items.filter(item=>can(item[2])&&
   (item[3]==='core'||canModule(item[3]))&&allowedBusiness(item[1]))
 })).filter(group=>group.items.length>0);

 const item=(label:string,to:string,Icon:any)=>{
  const node=<ListItemButton
   component={NavLink}
   to={to}
   end={to==='/platform'}
   onClick={mobile?()=>setMobileOpen(false):undefined}
   sx={{
    position:'relative',color:'rgba(255,255,255,.78)',minHeight:44,
    mx:compact?.75:1.25,mb:.25,px:compact?1.5:1.5,borderRadius:1,
    justifyContent:compact?'center':'flex-start',
    transition:'background-color 120ms ease,color 120ms ease',
    '&:hover':{bgcolor:'rgba(255,255,255,.08)',color:'#FFFFFF'},
    '&.active':{
     bgcolor:'rgba(255,255,255,.15)',color:'#FFFFFF',
     '&::before':{content:'""',position:'absolute',left:0,top:8,bottom:8,width:3,borderRadius:'0 4px 4px 0',bgcolor:currentPalette.gold}
    }
   }}
  >
   <ListItemIcon sx={{color:'inherit',minWidth:compact?0:40,justifyContent:'center'}}>
    <Icon fontSize="small"/>
   </ListItemIcon>
   {!compact&&<ListItemText primary={label} primaryTypographyProps={{fontSize:13.5,fontWeight:500}}/>}
  </ListItemButton>;
  return compact?<Tooltip key={to} title={label} placement="right">{node}</Tooltip>:<Box key={to}>{node}</Box>;
 };

 const content=<Box sx={{
  display:'flex',
  flexDirection:'column',
  height:'100%',
  bgcolor:currentPalette.navy,
  position:'relative',
  overflow:'visible'
 }}>
  <Toolbar sx={{px:compact?1:2,minHeight:84,justifyContent:compact?'center':'flex-start'}}>
   <Tooltip title="Go to home">
    <Box component={NavLink} to={platformSession?'/platform':'/'}
     onClick={mobile?()=>setMobileOpen(false):undefined}
     aria-label="Rasana — go to home"
     sx={{display:'inline-flex',alignItems:'center',textDecoration:'none',
      borderRadius:1,p:.5,m:-.5,
      '&:focus-visible':{outline:'2px solid',outlineColor:currentPalette.gold,outlineOffset:3}}}>
     {compact
      ?<RasanaLogo variant="mark" markSize={40}/>
      :<RasanaLogo variant="horizontal" markSize={42} inverse tagline={false}/>}
    </Box>
   </Tooltip>
  </Toolbar>

  {!mobile&&<Tooltip
   title={compact?'Expand navigation':'Collapse navigation'}
   placement="right"
  >
   <IconButton
    size="small"
    onClick={()=>changeCollapsed(!compact)}
    aria-label={compact?'Expand navigation':'Collapse navigation'}
    sx={{
     position:'absolute',
     top:2,
     right:2,
     zIndex:20,
     width:24,
     height:24,
     borderRadius:'15px',
     color:'rgba(255,255,255,.82)',
     bgcolor:alpha(currentPalette.navy,.98),
     border:'1px solid rgba(255,255,255,.16)',
     boxShadow:'0 3px 10px rgba(0,0,0,.20)',
     '& .MuiSvgIcon-root':{
      position:'relative',
      top:5
     },
     '&:hover':{
      color:'#FFFFFF',
      bgcolor:theme.palette.primary.main
     }
    }}
   >
    {compact
     ?<ChevronRightOutlined fontSize="small"/>
     :<ChevronLeftOutlined fontSize="small"/>
    }
   </IconButton>
  </Tooltip>}



  <Divider sx={{borderColor:'rgba(255,255,255,.09)'}}/>
  <Box sx={{flex:1,overflowY:'auto',overflowX:'hidden',py:1.25}}>
   {!platformSession&&<List disablePadding sx={{mb:.5}}>{item('Overview','/',DashboardOutlined)}</List>}
   {visibleGroups.map(group=><Box key={group.label} sx={{mt:compact?.5:1}}>
    {!compact&&<Typography variant="overline" sx={{px:2.75,py:.75,display:'block',color:'rgba(255,255,255,.38)'}}>
     {platformSession&&group.label==='Administration'?'Platform':group.label}
    </Typography>}
    <List dense disablePadding>{group.items.map(x=>item(x[0],x[1],x[4]))}</List>
   </Box>)}
   <Box sx={{mt:compact?.5:1}}>
    {!compact&&<Typography variant="overline" sx={{
     px:2.75,py:.75,display:'block',color:'rgba(255,255,255,.38)'}}>
     Help
    </Typography>}
    <List dense disablePadding>
     {item('User Manual',platformSession?'/platform/manual':'/manual',MenuBookOutlined)}
    </List>
   </Box>
  </Box>

  <Box sx={{mt:'auto',p:compact?1:2,bgcolor:'rgba(0,0,0,.08)'}}>
   <Divider sx={{mb:2,borderColor:'rgba(255,255,255,.09)'}}/>
   {compact
    ?<Tooltip placement="right" title={<Box><Typography variant="subtitle2">{s?.user?.name}</Typography><Typography variant="caption">{roleLabel}<br/>{s?.dataScope} scope<br/>{s?.organisation?.name}</Typography></Box>}>
      <Avatar sx={{width:38,height:38,mx:'auto',fontSize:13,fontWeight:700,bgcolor:theme.palette.primary.main,color:'#FFFFFF'}}>{initials(s?.user?.name)}</Avatar>
     </Tooltip>
    :<Stack direction="row" spacing={1.25} alignItems="center">
      <Avatar sx={{width:38,height:38,fontSize:13,fontWeight:700,bgcolor:theme.palette.primary.main,color:'#FFFFFF'}}>{initials(s?.user?.name)}</Avatar>
      <Box sx={{minWidth:0}}>
       <Typography variant="body2" fontWeight={600} noWrap sx={{color:'#FFFFFF'}}>{s?.user?.name}</Typography>
       <Typography variant="caption" noWrap sx={{color:'rgba(255,255,255,.66)',display:'block'}}>{roleLabel}</Typography>
       <Typography variant="caption" noWrap sx={{color:'rgba(255,255,255,.42)',display:'block'}}>{platformSession?'Rasana Platform':`${s?.dataScope} scope · ${s?.organisation?.name}`}</Typography>
      </Box>
     </Stack>}
   <Stack direction="row" justifyContent={compact?'center':'flex-end'} spacing={1} sx={{mt:1}}>
    <Tooltip title="Appearance preferences" placement="top">
     <IconButton aria-label="Appearance preferences" onClick={()=>setAppearanceOpen(true)}
      sx={{color:'rgba(255,255,255,.78)','&:hover':{color:'#FFFFFF',bgcolor:'rgba(255,255,255,.08)'}}}>
      <PaletteOutlined fontSize="small"/>
     </IconButton>
    </Tooltip>
    <Tooltip title="Sign out" placement="top">
     <IconButton aria-label="Sign out" onClick={logout}
      sx={{color:'rgba(255,255,255,.78)','&:hover':{color:'#FFFFFF',bgcolor:'rgba(255,255,255,.08)'}}}>
      <LogoutOutlined fontSize="small"/>
     </IconButton>
    </Tooltip>
   </Stack>
   <Divider sx={{mt:1.5,mb:1,borderColor:'rgba(255,255,255,.09)'}}/>
   {compact
    ?<Tooltip arrow placement="right" title={RASANA_BUILD.tooltip}>
      <Typography variant="caption" sx={{
       display:'block',textAlign:'center',color:'rgba(255,255,255,.65)',
       fontSize:10
      }}>
       v{RASANA_BUILD.version}
      </Typography>
     </Tooltip>
    :<Tooltip arrow placement="right" title={RASANA_BUILD.tooltip}>
      <Box sx={{px:0.5}}>
       <Typography variant="caption" sx={{
        display:'block',fontWeight:600,color:'rgba(255,255,255,.75)',
        fontSize:11
       }}>
        {RASANA_BUILD.label}
       </Typography>
       <Typography variant="caption" sx={{
        display:'block',color:'rgba(255,255,255,.50)',fontSize:11
       }}>
        {RASANA_BUILD.buildLabel}
       </Typography>
      </Box>
     </Tooltip>}
  </Box>
 </Box>;

 return <Box sx={{display:'flex',minHeight:'100vh',bgcolor:'background.default'}}>
  <GlobalActivity/>
  <Dialog open={appearanceOpen} onClose={()=>setAppearanceOpen(false)} fullWidth maxWidth="xs">
   <DialogTitle>Appearance preferences</DialogTitle>
   <DialogContent>
    <Typography variant="body2" color="text.secondary" sx={{mb:2}}>
     Choose your workspace appearance on this browser. The public sign-in page keeps Rasana branding consistent on every device.
    </Typography>
    <Stack spacing={1}>
     {loginAppearanceOptions.map(option=>
      <Button key={option.id} variant={loginAppearance===option.id?'contained':'outlined'}
       aria-pressed={loginAppearance===option.id}
       onClick={()=>updateLoginAppearance(option.id)}
       sx={{justifyContent:'flex-start',textTransform:'none'}}>
       {option.label} — {option.detail}
      </Button>
     )}
    </Stack>
   </DialogContent>
   <DialogActions><Button onClick={()=>setAppearanceOpen(false)}>Done</Button></DialogActions>
  </Dialog>
  {mobile&&<AppBar position="fixed" elevation={0} sx={{bgcolor:currentPalette.navy,borderBottom:'1px solid rgba(255,255,255,.08)'}}>
   <Toolbar>
    <IconButton edge="start" color="inherit" onClick={()=>setMobileOpen(true)} aria-label="Open navigation"><MenuOutlined/></IconButton>
    <Box component={NavLink} to={platformSession?'/platform':'/'}
     aria-label="Rasana — go to home" onClick={()=>setMobileOpen(false)}
     sx={{ml:1,display:'inline-flex',alignItems:'center',textDecoration:'none',borderRadius:1,
      '&:focus-visible':{outline:'2px solid',outlineColor:currentPalette.gold,outlineOffset:3}}}>
     <RasanaLogo variant="horizontal" markSize={32} inverse tagline={false}/>
    </Box>
    <Typography variant="caption" sx={{ml:'auto',color:'rgba(255,255,255,.68)',maxWidth:{xs:85,sm:220},overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s?.user?.name}</Typography>
   </Toolbar>
  </AppBar>}
  <Drawer
   variant={mobile?'temporary':'permanent'} open={mobile?mobileOpen:true} onClose={()=>setMobileOpen(false)} ModalProps={{keepMounted:true}}
   sx={{width:mobile?expandedWidth:drawerWidth,flexShrink:0,'& .MuiDrawer-paper':{width:mobile?expandedWidth:drawerWidth,boxSizing:'border-box',bgcolor:currentPalette.navy,color:'white',overflowX:mobile?'hidden':'visible',borderRight:0,transition:theme.transitions.create('width',{duration:theme.transitions.duration.shorter})}}}
  >{content}</Drawer>
  <Box component="main" sx={{flexGrow:1,minWidth:0,bgcolor:theme.palette.background.default,px:{xs:1.5,sm:2,md:3.5,xl:4},pb:{xs:3,md:4},pt:{xs:10,md:3.5}}}>
   <Box sx={{width:'100%',maxWidth:1600,mx:'auto'}}>
    {onboarding&&<Alert severity="info" sx={{mb:1.5,py:.25}}
     action={<Button color="inherit" size="small" disabled={returning}
      onClick={()=>void returnToPlatform()}>{returning?'Returning…':'Return to Onboarding'}</Button>}>
     Onboarding · <strong>{s?.organisation?.name}</strong> · {onboarding.phase}
     {' · '}Until {new Date(onboarding.expiresAt).toLocaleTimeString([],
      {hour:'2-digit',minute:'2-digit'})}
    </Alert>}
    {support&&<Alert severity="warning" sx={{mb:1.5,py:.25}}
     action={<Button color="inherit" size="small" disabled={returning}
      onClick={()=>void returnToPlatform()}>{returning?'Returning…':'Return to Platform'}</Button>}>
     Temporary support access · Until {new Date(support.expiresAt).toLocaleTimeString([],
      {hour:'2-digit',minute:'2-digit'})}
    </Alert>}
    {s?.subscription?.readOnly&&<Alert severity="warning" sx={{mb:1.5}}>
     Trial expired · This workspace is read-only.
    </Alert>}
    {!modulesVerified&&!platformSession&&!onboarding&&!support
     ?<Alert severity="warning">Verifying Department module access. Reconnect to continue.</Alert>
     :!allowedBusiness(location.pathname)
      ?<Alert severity="warning" action={<Button href="/" color="inherit">Home</Button>}>
        This module is not available for your Department.
       </Alert>
      :<Suspense fallback={<RouteLoadingState/>}><Outlet/></Suspense>}
   </Box>
  </Box>
 </Box>;
}
